/**
 * PlacesGenealogyPage — /places/india, /places/india/:state,
 * /places/india/:state/:district, /places/india/:state/:district/:place
 *
 * The Kerala Place Pilot. Fed by /api/genealogy/places/*, only surfaces
 * verified places (India → Kerala → Kottayam → Pala for now).
 * Everything else returns 404 with noindex.
 *
 * Fully SEO-tuned: canonical URL, Malayalam alt-slug hreflang, Place +
 * BreadcrumbList schema, dynamic OG. Mobile-first responsive.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { http } from "@/lib/api";
import { MapPin, TreePine, Users, Flame, ArrowRight, Loader2, Home as HomeIcon } from "lucide-react";

const P = {
  ink: "#1F1A15",
  inkSoft: "#5E4E3A",
  paper: "#FFF9EF",
  rule: "#D9C79A",
  gold: "#B58936",
  saffron: "#B25D38",
  tulsi: "#0E7C55",
  card: "#FFFDF6",
};

function usePath() {
  const params = useParams();
  return [params.country, params.state, params.district, params.place].filter(Boolean);
}

function Breadcrumbs({ parts }) {
  const links = [];
  let acc = "";
  for (const p of parts) {
    acc = acc ? `${acc}/${p}` : p;
    links.push({ slug: p, href: `/places/${acc}` });
  }
  return (
    <nav className="text-[11px] font-mono uppercase tracking-widest flex flex-wrap items-center gap-1" style={{ color: P.inkSoft }} data-testid="places-breadcrumbs" aria-label="Breadcrumb">
      <Link to="/places/india" className="hover:underline" style={{ color: P.saffron }}>Places</Link>
      {links.map((l, i) => (
        <span key={l.href} className="flex items-center gap-1">
          <span aria-hidden>›</span>
          <Link to={l.href} className="hover:underline capitalize" style={{ color: P.saffron }}>{l.slug}</Link>
        </span>
      ))}
    </nav>
  );
}

export default function PlacesGenealogyPage() {
  const parts = usePath();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiPath = useMemo(() => `/genealogy/places/${parts.join("/") || "india"}`, [parts]);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null); setData(null);
    (async () => {
      try {
        const { data } = await http.get(apiPath);
        if (alive) setData(data);
      } catch (e) {
        if (alive) setError(e?.response?.status === 404 ? "not_verified" : "error");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [apiPath]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" style={{ background: P.paper }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: P.saffron }} />
      </div>
    );
  }
  if (error === "not_verified" || !data) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center" style={{ background: P.paper, color: P.ink }}>
        <Helmet><title>Place not yet verified — IndianFamilyTree</title><meta name="robots" content="noindex,follow" /></Helmet>
        <div className="text-5xl" style={{ color: P.gold }}>❦</div>
        <h1 className="mt-4 font-display text-3xl">This place isn't part of the pilot yet</h1>
        <p className="mt-2 max-w-md text-sm" style={{ color: P.inkSoft }}>
          We're launching the Places directory one verified region at a time, starting with{" "}
          <Link to="/places/india/kerala/kottayam/pala" className="underline" style={{ color: P.saffron }}>Pala, Kottayam, Kerala</Link>.{" "}
          Want to bring your ancestral village online?{" "}
          <Link to="/celebrate/submit" className="underline" style={{ color: P.saffron }}>Contact the archive team</Link>.
        </p>
      </div>
    );
  }

  const isLeaf = data.kind === "place";
  const path = parts.join("/");
  const canonicalUrl = `https://www.indianfamilytree.com/places/${path}`;
  const title = `${data.label}${data.label_ml ? ` (${data.label_ml})` : ""} — Families, memorials & genealogy · IndianFamilyTree`;
  const description = data.summary || `Family trees, memorials and members with ancestral roots in ${data.label}.`;
  const bcListItems = ["Places", ...parts.map((p, i) => (
    // capitalize slug for the visible label
    p.charAt(0).toUpperCase() + p.slice(1)
  ))];

  return (
    <div className="min-h-screen" style={{ background: P.paper, color: P.ink }}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index,follow" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        {/* Malayalam alt-slug hreflang — Pala has slug_ml `പാല`; only
            emit when the API provides the native-script slug. */}
        {data.slug_ml && (
          <link rel="alternate" hrefLang="ml-IN" href={`${canonicalUrl.replace(/\/[^/]+$/, "")}/${encodeURIComponent(data.slug_ml)}`} />
        )}
        <link rel="alternate" hrefLang="en-IN" href={canonicalUrl} />
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Place",
          "name": data.label,
          "alternateName": data.label_ml || undefined,
          "description": data.summary,
          "geo": (data.lat && data.lon) ? { "@type": "GeoCoordinates", "latitude": data.lat, "longitude": data.lon } : undefined,
          "url": canonicalUrl,
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": bcListItems.map((label, i) => ({
            "@type": "ListItem", "position": i + 1, "name": label,
            "item": i === 0 ? "https://www.indianfamilytree.com/places/india" : `https://www.indianfamilytree.com/places/${parts.slice(0, i).join("/")}`,
          })),
        })}</script>
      </Helmet>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-14">
        <Breadcrumbs parts={parts} />
        <div className="mt-4 flex flex-wrap items-end gap-4">
          <h1 className="font-display italic text-4xl sm:text-5xl lg:text-6xl leading-tight" style={{ color: P.ink }} data-testid="places-h1">
            {data.label}
          </h1>
          {data.label_ml && (
            <div className="text-2xl sm:text-3xl" lang="ml" style={{ color: P.saffron, fontFamily: "'Noto Sans Malayalam', 'Manjari', system-ui, sans-serif" }} data-testid="places-label-ml">
              {data.label_ml}
            </div>
          )}
          <span className="inline-block px-2 py-1 text-[10px] font-mono uppercase tracking-widest rounded-full border" style={{ borderColor: P.tulsi, color: P.tulsi }} data-testid="places-kind">
            {data.kind}
          </span>
        </div>

        {data.summary && (
          <p className="mt-4 max-w-3xl text-base sm:text-lg leading-relaxed" style={{ color: P.inkSoft }} data-testid="places-summary">
            {data.summary}
          </p>
        )}

        {/* Counts */}
        <div className="mt-8 grid grid-cols-3 gap-3 sm:gap-4">
          {[
            { icon: TreePine, label: "Families", value: data.counts.trees, color: P.saffron },
            { icon: Users, label: "People", value: data.counts.members, color: P.tulsi },
            { icon: Flame, label: "Memorials", value: data.counts.memorials, color: P.gold },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl border-2 p-4 sm:p-5 text-center" style={{ borderColor: `${P.gold}55`, background: P.card }} data-testid={`places-count-${s.label.toLowerCase()}`}>
              <s.icon className="w-5 h-5 mx-auto" style={{ color: s.color }} />
              <div className="mt-1 font-display text-2xl sm:text-3xl" style={{ color: P.ink }}>{s.value}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest mt-1" style={{ color: P.inkSoft }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Children (higher-level pages) */}
        {!isLeaf && data.children && data.children.length > 0 && (
          <section className="mt-10" data-testid="places-children">
            <h2 className="font-display text-2xl sm:text-3xl" style={{ color: P.ink }}>Explore within {data.label}</h2>
            <div className="mt-4 grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-3">
              {data.children.map((c) => (
                <Link
                  key={c.slug}
                  to={`/places/${path}/${c.slug}`}
                  className="block rounded-xl border-2 p-4 hover:shadow-md transition-all active:scale-[0.99]"
                  style={{ borderColor: `${P.gold}44`, background: P.card }}
                  data-testid={`places-child-${c.slug}`}
                >
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 flex-none" style={{ color: P.saffron }} />
                    <span className="font-display text-lg" style={{ color: P.ink }}>{c.label}</span>
                    {c.label_ml && <span lang="ml" className="ml-auto text-sm" style={{ color: P.inkSoft, fontFamily: "'Noto Sans Malayalam', 'Manjari', system-ui, sans-serif" }}>{c.label_ml}</span>}
                  </div>
                  <div className="mt-1 text-[10px] font-mono uppercase tracking-widest opacity-70">{c.kind}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Leaf page: samples */}
        {isLeaf && (
          <>
            {data.sample_trees && data.sample_trees.length > 0 && (
              <section className="mt-10" data-testid="places-sample-trees">
                <h2 className="font-display text-2xl sm:text-3xl" style={{ color: P.ink }}>Families of {data.label}</h2>
                <div className="mt-4 grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                  {data.sample_trees.map((t) => (
                    <Link key={t.id} to={`/family/${t.slug || t.id}`} className="block rounded-xl border-2 p-4 hover:shadow-md" style={{ borderColor: `${P.gold}44`, background: P.card }} data-testid={`places-tree-${t.slug || t.id}`}>
                      <div className="flex items-center gap-2">
                        <TreePine className="w-4 h-4" style={{ color: P.saffron }} />
                        <span className="font-display text-lg truncate">{t.name}</span>
                      </div>
                      {t.origin_location && (
                        <div className="mt-1 text-xs flex items-center gap-1" style={{ color: P.inkSoft }}>
                          <HomeIcon className="w-3 h-3" /> {t.origin_location}
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {data.sample_memorials && data.sample_memorials.length > 0 && (
              <section className="mt-10" data-testid="places-sample-memorials">
                <h2 className="font-display text-2xl sm:text-3xl" style={{ color: P.ink }}>Remembered in {data.label}</h2>
                <div className="mt-4 grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
                  {data.sample_memorials.map((m) => {
                    const name = m.preferred_name || m.name_latin || m.full_name || "In loving memory";
                    return (
                      <Link key={m.slug || m.id} to={`/memorial/${m.slug || m.id}`} className="block rounded-xl border-2 p-3 hover:shadow-md" style={{ borderColor: `${P.gold}44`, background: P.card }} data-testid={`places-memorial-${m.slug || m.id}`}>
                        <div className="flex items-start gap-2">
                          {m.portrait ? (
                            <img src={m.portrait} alt={name} className="w-10 h-10 rounded-full object-cover border" style={{ borderColor: P.gold }} loading="lazy" />
                          ) : (
                            <div className="w-10 h-10 rounded-full flex items-center justify-center border" style={{ borderColor: P.gold, background: "#F5EBD0", color: P.saffron }}>
                              <Flame className="w-4 h-4" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-display truncate">{name}</div>
                            {m.age_at_death && <div className="text-[10px] opacity-70 italic">{m.age_at_death} yrs</div>}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}

            {data.sample_members && data.sample_members.length > 0 && (
              <section className="mt-10" data-testid="places-sample-members">
                <h2 className="font-display text-2xl sm:text-3xl" style={{ color: P.ink }}>Notable people from {data.label}</h2>
                <p className="text-xs opacity-70 mt-1">Public / deceased profiles only. Living persons are private by default.</p>
                <div className="mt-4 grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
                  {data.sample_members.map((m) => {
                    const nm = [m.first_name, m.last_name].filter(Boolean).join(" ");
                    return (
                      <div key={m.id} className="rounded-xl border p-3" style={{ borderColor: `${P.gold}33`, background: P.card }} data-testid={`places-member-${m.id}`}>
                        <div className="text-sm font-display truncate">{nm || "—"}</div>
                        {m.name_ml && <div lang="ml" className="text-xs mt-0.5" style={{ color: P.inkSoft, fontFamily: "'Noto Sans Malayalam', 'Manjari', system-ui, sans-serif" }}>{m.name_ml}</div>}
                        {m.birth_date && <div className="text-[10px] opacity-70 mt-1 font-mono tabular-nums">b. {m.birth_date}</div>}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </>
        )}

        {/* CTA */}
        <div className="mt-14 rounded-2xl p-6 sm:p-8 text-center" style={{ background: `${P.saffron}0F`, border: `1px solid ${P.saffron}55` }}>
          <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: P.saffron }}>Contribute</div>
          <div className="mt-1 font-display text-2xl sm:text-3xl">Are you rooted in {data.label}?</div>
          <p className="mt-2 max-w-xl mx-auto text-sm" style={{ color: P.inkSoft }}>
            Add your family tree and help build the definitive record of families with ancestral ties to {data.label}.
          </p>
          <Link
            to="/family-trees/new"
            data-testid="places-start-tree-cta"
            className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-white text-sm font-semibold shadow hover:opacity-90"
            style={{ background: P.saffron }}
          >
            Start your family tree <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
